import { gql, useMutation, useQuery } from '@apollo/client'
import { CardCvcElement, CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { FormikActions } from 'formik'
import getConfig from 'next/config'
import { Dispatch } from 'react'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { IBillingAddress } from 'checkout/components/PaymentMethodPanel/NewCardNewAddressForm'
import {
  IAction,
  ICreditCardSuccessAction,
  IErrorAction,
  IPanelUpdateAction,
} from 'checkout/containers/CheckoutStateReducer'
import { ORDER_PROPS, PROMOTION_PROPS, SHIPPING_ADDRESS_COUNT } from 'checkout/hooks/useOrder'
import { PartialOrder } from 'checkout/types/IOrder'
import isAllCredits from 'checkout/utils/isAllCredits'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { addressErrorFieldMap } from 'shared/addressValidations'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useAuth } from 'shared/hooks/useAuth'
import useCurrency from 'shared/hooks/useCurrency'
import { useUser } from 'shared/hooks/useUser'
import { isTimeoutError, timeoutMessage } from '../utils/timeout'

const TP = 'checkout.hooks.usePayments'

const getConnectBillingInfo = (promotionsEnabled: boolean) => gql`
  mutation connectBillingInfo($input: ConnectBillingInfoInput!, $shippingAddressCount: Int) {
    connectBillingInfo(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const getCreateStripePayment = (promotionsEnabled: boolean) => gql`
  mutation createStripePaymentWithBilling($input: CreateStripePaymentWithBillingInput!, $shippingAddressCount: Int) {
    createStripePaymentWithBilling(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const VERIFY_CREDIT_CARD = gql`
  mutation verifyCreditCard($input: VerifyCreditCardInput!) {
    verifyCreditCard(input: $input)
  }
`

const CREATE_AFTER_PAY_TOKEN = gql`
  mutation createAfterPayToken($input: CreateAfterPayTokenInput!) {
    createAfterPayToken(input: $input)
  }
`

const CREATE_KLARNA_PAYMENT_SESSION = gql`
  mutation createKlarnaPaymentSession($input: CreateKlarnaPaymentSessionInput!) {
    createKlarnaPaymentSession(input: $input)
  }
`
const CREATE_PAYPAL_V2_PAYMENT_SESSION = gql`
  mutation createPayPalv2PaymentSession($input: CreatePayPalv2PaymentSessionInput!) {
    createPayPalv2PaymentSession(input: $input) {
      paypalCheckoutLink
    }
  }
`

export const GET_CREDIT_CARDS = gql`
  query getCreditCards {
    creditCards {
      id
      name
      last4Digits
      cardBrand
      paymentType
      billingAddressId
    }
  }
`

const ORDER_WITH_KLARNA = gql`
  mutation buyKlarna($input: BuyKlarnaInput!, $shippingAddressCount: Int) {
    buyKlarna(input: $input) {
      ...OrderProps
    }
  }
  ${ORDER_PROPS}
`
const ORDER_WITH_PAYPAL_V2 = gql`
  mutation buyPayPalv2($input: BuyPayPalv2Input!, $shippingAddressCount: Int) {
    buyPayPalv2(input: $input) {
      ...OrderProps
    }
  }
  ${ORDER_PROPS}
`

export interface ICreditCard {
  id: number
  name: string
  last4Digits: string
  cardBrand: string
  paymentType: string
  billingAddressId: number
}

interface IUsePaymentsProps {
  dispatch: Dispatch<IAction | ICreditCardSuccessAction | IPanelUpdateAction | IErrorAction>
  order?: PartialOrder
}

const usePayments = ({ dispatch, order }: IUsePaymentsProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { currentUser } = useUser()
  const { logoutUser } = useAuth()

  const promotionsEnabled = useFeatureFlag(FeatureFlag.PROMO_CODES_USER)
  const isExpressKlarnaEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_KLARNA)
  const [connectBillingInfo] = useMutation(getConnectBillingInfo(promotionsEnabled))
  const [createAfterPayToken] = useMutation(CREATE_AFTER_PAY_TOKEN)
  const [verifyCreditCardMutation] = useMutation(VERIFY_CREDIT_CARD)
  const [createStripePayment] = useMutation(getCreateStripePayment(promotionsEnabled), {
    refetchQueries: [{ query: GET_CREDIT_CARDS }],
  })
  const { refetch: getCreditCardsQuery } = useQuery(GET_CREDIT_CARDS, { skip: true })
  const { selectedCurrency } = useCurrency()

  const [createKlarnaPaymentSession] = useMutation(CREATE_KLARNA_PAYMENT_SESSION)
  const [orderWithKlarna] = useMutation(ORDER_WITH_KLARNA)

  const [createPayPalv2PaymentSession] = useMutation(CREATE_PAYPAL_V2_PAYMENT_SESSION)
  const [orderWithPayPalv2] = useMutation(ORDER_WITH_PAYPAL_V2)

  const getCreditCards = async () => {
    dispatch({ type: 'paymentMethodRequest' })
    try {
      const { data } = await getCreditCardsQuery()
      dispatch({ type: 'creditCardRequestSuccess', payload: data?.creditCards })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({ type: 'paymentMethodRequestFailure', payload: error.message })
    }
  }

  const selectAfterPay = async () => {
    if (!order?.orderNumber || !order.finalPrice?.value) {
      const error = t(
        `${TP}.selectAfterPayError`,
        'No order number present when user selects AfterPay, in select payment method',
      )
      Bugsnag.notify(new Error(error))
      throw new Error(error)
    }
    try {
      window.AfterPay.initialize({ countryCode: 'US' })
      const { data } = await createAfterPayToken({
        variables: {
          input: {
            orderNumber: order.orderNumber,
            finalPriceCents: order.finalPrice.value,
            redirectConfirmUrl: `${location.origin}/checkout/${order.orderNumber}/afterpay-webhook`,
            redirectCancelUrl: `${location.origin}/checkout/${order.orderNumber}`,
          },
        },
      })
      const token = data?.createAfterPayToken
      window.AfterPay.redirect({ token })
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      dispatch({ type: 'paymentMethodRequestFailure', payload: error.message })
    }
  }

  const selectAffirm = () => {
    const { publicRuntimeConfig } = getConfig()
    const orderNumber = order?.orderNumber
    const productTemplate = order?.product.productTemplate
    const shippingAddress = order?.shippingAddress
    if (!orderNumber || !productTemplate || !shippingAddress) {
      const error = t(
        `${TP}.selectAffirmError`,
        'No order number present when user selects affirm, in selectAffirm, paymentMethodService',
      )
      Bugsnag.notify(new Error(error))
      throw new Error(error)
    }
    if (isAllCredits(order)) {
      // we shouldn't use affirm if the order has all credits
      return null
    }
    window.affirm.checkout({
      config: {
        financial_product_key: publicRuntimeConfig.affirmFinancialKey,
      },
      merchant: {
        user_cancel_url: `${window.location.origin}/checkout/${orderNumber}?status=CANCELLED`,
        user_confirmation_url: `${window.location.origin}/checkout/${orderNumber}/affirm-webhook`,
        user_confirmation_url_action: 'GET',
      },
      shipping: {
        name: {
          full: shippingAddress.name,
        },
        address: {
          line1: shippingAddress.address1,
          line2: shippingAddress.address2 || '',
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipcode: shippingAddress.postalCode,
        },
      },
      items: [
        {
          display_name: productTemplate.name,
          sku: productTemplate.sku,
          unit_price: order?.productPrice?.value,
          qty: 1,
          item_image_url: productTemplate.pictureUrl,
          item_url: `${window.location.origin}/sneakers/${order?.product.productTemplate.slug}`,
        },
      ],
      currency: 'USD',
      tax_amount: order?.taxPrice?.value,
      shipping_amount: order?.shippingPrice?.value,
      total: order?.finalPrice?.value,
    })
    window.affirm.checkout.open()
  }

  const selectKlarna = async () => {
    try {
      dispatch({ type: 'paymentMethodRequest' })
      const input = {
        orderNumber: order?.orderNumber,
        finalPriceCents: order?.finalPrice?.value,
        email: currentUser?.email,
      }
      const { data } = await orderWithKlarna({
        variables: { input, shippingAddressCount: SHIPPING_ADDRESS_COUNT },
      })
      dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: data.buyKlarna,
      })
      createKlarnaClientSession()
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: error.message,
      })
    }
  }

  const createKlarnaClientSession = async () => {
    const { orderNumber } = order || {}
    const {
      location: { origin },
    } = window
    const klarnaSuccessUrl = isExpressKlarnaEnabled
      ? `${origin}/checkout/${orderNumber}/klarna-webhook`
      : `${origin}/checkout/${orderNumber}?klarna=success`

    try {
      const { data } = await createKlarnaPaymentSession({
        variables: {
          input: {
            orderNumber,
            countryCode: 'us', // selectedCurrency?.isoCode,
            merchantUrls: {
              success: klarnaSuccessUrl,
              cancel: `${origin}/checkout/${orderNumber}?klarna=cancel`,
              back: `${origin}/checkout/${orderNumber}?klarna=back`,
              failure: `${origin}/checkout/${orderNumber}?klarna=failure`,
              error: `${origin}/checkout/${orderNumber}?klarna=error`,
            },
          },
        },
      })
      // GraphQL types throwing me for a loop, let me know how to do this better
      if (data?.error) {
        // dispatch generic error notification
      } else {
        const parsedData = JSON.parse(data?.createKlarnaPaymentSession)
        window.location.href = parsedData.redirectUrl
      }
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: error.message,
      })
    }
  }

  const selectPayPalv2 = async () => {
    try {
      dispatch({ type: 'paymentMethodRequest' })
      const input = {
        orderNumber: order?.orderNumber,
        email: currentUser?.email,
      }
      await orderWithPayPalv2({
        // Why do we need shippingAddressCount?
        variables: { input, shippingAddressCount: SHIPPING_ADDRESS_COUNT },
      })

      const paypalCheckoutLink = await createPayPalv2ClientSession()

      if (!paypalCheckoutLink) {
        throw new Error('Invalid PayPal checkout link.')
      }

      window.location.href = paypalCheckoutLink
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: error.message,
      })
    }
  }

  // Tell PayPal what URLs to redirect the user to after payment or cancellation
  // PayPal returns an external checkout link that we redirect the user to.
  const createPayPalv2ClientSession = async () => {
    try {
      const { orderNumber } = order || {}
      const redirectConfirmUrl =
        `${window.location.origin}/checkout/${orderNumber}/paypal-webhook` +
        `?orderNumber=${orderNumber}`
      const redirectCancelUrl = `${window.location.origin}/checkout/${orderNumber}?paypal=cancel`
      const { data } = await createPayPalv2PaymentSession({
        variables: {
          input: {
            orderNumber,
            redirectConfirmUrl,
            redirectCancelUrl,
          },
        },
      })

      if (typeof data?.createPayPalv2PaymentSession?.paypalCheckoutLink !== 'string') {
        throw new Error('Invalid PayPal checkout link type.')
      }

      const paypalCheckoutLink = new URL(data?.createPayPalv2PaymentSession?.paypalCheckoutLink)

      if (!paypalCheckoutLink?.host?.includes('paypal.com')) {
        throw new Error('Invalid PayPal checkout link host.')
      }

      return paypalCheckoutLink.toString()
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: error.message,
      })
    }
  }

  const selectCreditCard = async (creditCardId: number) => {
    if (creditCardId === order?.billingInfo?.id) {
      return dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: order,
      })
    }
    try {
      dispatch({ type: 'paymentMethodRequest' })
      const { data } = await connectBillingInfo({
        variables: {
          input: {
            orderNumber: order?.orderNumber,
            billingInfoId: creditCardId?.toString(),
          },
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: data?.connectBillingInfo,
      })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: error.message,
      })
    }
  }

  const addNewCreditCard = async (
    address: IBillingAddress,
    formikActions: FormikActions<IBillingAddress>,
  ) => {
    const cardElement = elements?.getElement(CardElement)
    for (const [key, value] of Object.entries(address)) {
      if (!value) {
        delete address[key]
      }
    }
    try {
      if (!order?.orderNumber) {
        throw new Error(
          t(`${TP}.noOrderNumberError`, "No order number, can't create stripe payment"),
        )
      }
      if (!stripe || !cardElement) {
        throw new Error(t(`${TP}.noCardError`, 'Can not add new card. Stripe is unavailable'))
      }
      dispatch({ type: 'paymentMethodRequest' })
      const { token, error } = await stripe.createToken(cardElement, {
        name: address.name,
        address_line1: address.address1,
        address_line2: address.address2 || '',
        address_city: address.city,
        address_state: address.state,
        address_zip: address.postalCode,
        address_country: address.countryCode,
      })
      const stripeToken = token?.id
      if (error) {
        throw error
      }
      if (!stripeToken) {
        throw new Error(
          t(`${TP}.noOrderNumberError`, {
            defaultValue: "No stripe token, can't create stripe payment for {orderNumber}",
            orderNumber: order.orderNumber,
          }),
        )
      }
      const { data } = await createStripePayment({
        variables: {
          input: {
            name: address.name,
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
            countryCode: address.countryCode,
            phone: address.phone,
            stripeToken,
            orderNumber: order.orderNumber,
          },
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: data?.createStripePaymentWithBilling,
      })
    } catch (err) {
      Bugsnag.notify(err as NotifiableError)
      if (isTimeoutError(err)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      const errorMessage = err.message.replace('GraphQL error: ', '')
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'paymentMethodRequestFailure',
          payload: t(`${TP}.paymentMethodRequestFailure`, 'Please update the fields below'),
        })
        return
      }
      if (/address/i.test(errorMessage)) {
        formikActions.setFieldError(
          'address1',
          t(`${TP}.addressFieldError`, 'Please update your address'),
        )
      }
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: err.message,
      })
    }
  }

  const verifyCreditCard = async () => {
    const cvcElement = elements?.getElement(CardCvcElement)
    try {
      if (!order?.orderNumber) {
        const errorMessage = t(
          `${TP}.noOrderNumberError2`,
          'No order available. Can not verify credit card.',
        )
        Bugsnag.notify(new Error(errorMessage))
        throw new Error(errorMessage)
      }
      if (!stripe || !cvcElement) {
        const errorMessage = t(
          `${TP}.noCvcError`,
          'Unable to verify your credit card. Stripe is unavailable',
        )
        Bugsnag.notify(new Error(errorMessage))
        throw new Error(errorMessage)
      }
      dispatch({ type: 'paymentMethodRequest' })
      const { token, error } = await stripe.createToken('cvc_update', cvcElement)
      const stripeToken = token?.id
      if (error) {
        throw error
      }
      if (!stripeToken) {
        throw new Error(
          t(`${TP}.stripeTokenError`, {
            defaultValue: "No stripe token, can't verify credit card for order {orderNumber}",
            orderNumber: order.orderNumber,
          }),
        )
      }
      const { data } = await verifyCreditCardMutation({
        variables: { input: { token: stripeToken, orderNumber: order.orderNumber } },
      })
      sendTrackingEvent('CHECKOUT_CVV_SUBMIT_TAP', {
        condition: order?.product.shoeCondition,
        error_message: null,
        instant_ship: order?.isInstantShip,
        price: order?.productPrice?.value,
        product_template_slug: order?.product.productTemplate.slug,
        size: order?.product?.size?.value,
        is_apparel_product: order?.product?.productTemplate?.productCategory !== 'shoes',
        currency_code: selectedCurrency?.isoCode,
      })
      dispatch({
        type: 'verifyCreditCardSuccess',
        payload: data?.verifyCreditCard,
      })
    } catch (err) {
      if (isTimeoutError(err)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      Bugsnag.notify(err as NotifiableError)
      sendTrackingEvent('CHECKOUT_CVV_SUBMIT_TAP', {
        condition: order?.product.shoeCondition,
        error_message: err.message,
        instant_ship: order?.isInstantShip,
        price: order?.productPrice?.value,
        product_template_slug: order?.product.productTemplate.slug,
        size: order?.product?.size?.value,
        is_apparel_product: order?.product?.productTemplate?.productCategory !== 'shoes',
        currency_code: selectedCurrency?.isoCode,
      })
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: err.message,
      })
    }
  }

  return {
    addNewCreditCard,
    getCreditCards,
    selectAffirm,
    selectAfterPay,
    selectKlarna,
    selectPayPalv2,
    selectCreditCard,
    verifyCreditCard,
  }
}

export default usePayments
